import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, catchError, Observable, of, Subject, switchMap, take, tap, timeout } from 'rxjs';
import { CallDirectoryContact } from './call-directory.plugin';

const HASH_KEY = 'call_directory_hash';
const COUNT_KEY = 'call_directory_count';

export type CallDirectorySyncStatus = 'idle' | 'syncing' | 'synced' | 'up-to-date' | 'error';

@Injectable()
export class CallDirectorySyncService {

  private syncResult$ = new Subject<any>();

  readonly status$ = new BehaviorSubject<CallDirectorySyncStatus>('idle');
  readonly lastSynced$ = new BehaviorSubject<number | null>(this.restoreCount());

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
  ) {
    window.addEventListener('callDirectoryResult', (event: any) => {
      this.ngZone.run(() => {
        const data = typeof event === 'string' ? JSON.parse(event) : event;
        console.log('[CallDirectory] Native result:', JSON.stringify(data));
        this.syncResult$.next(data);
      });
    });
  }

  clear(): Observable<any> {
    console.log('[CallDirectory] Clearing contacts...');
    localStorage.removeItem(HASH_KEY);

    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return this.syncIOS([]);
    } else if (platform === 'android') {
      return this.syncAndroid([]);
    }

    return of({ cleared: true });
  }

  sync(): Observable<any> {
    if (this.status$.value === 'syncing') {
      return of({ synced: 0, skipped: true });
    }
    this.status$.next('syncing');

    return this.http.get<{ hash: string }>('/api/call-directory/contacts/hash').pipe(
      switchMap(({ hash }) => this.checkHashAndSync(hash)),
      tap(res => {
        if (res?.skipped) {
          this.status$.next('up-to-date');
        } else {
          const count = res?.synced ?? 0;
          this.lastSynced$.next(count);
          this.saveCount(count);
          this.status$.next('synced');
        }
      }),
      catchError(err => {
        console.warn('[CallDirectory] sync error:', err?.status, err?.url, err?.message, JSON.stringify(err?.error));
        this.status$.next('error');

        return of(undefined);
      })
    );
  }

  private checkHashAndSync(serverHash: string): Observable<any> {
    const localHash = localStorage.getItem(HASH_KEY);
    if (localHash === serverHash) {
      console.log('[CallDirectory] Hash unchanged, skipping sync');
      return of({ synced: 0, skipped: true });
    }

    console.log('[CallDirectory] Hash changed, syncing...');
    return this.fetchAndSync(serverHash);
  }

  private fetchAndSync(serverHash: string): Observable<any> {
    return this.http.get<CallDirectoryContact[]>('/api/call-directory/contacts').pipe(
      tap(contacts => {
        console.log(`[CallDirectory] Fetched ${contacts.length} contacts`);

      }),
      switchMap(contacts => {
        if (Capacitor.getPlatform() === 'ios') {
          return this.syncIOS(contacts);
        } else if (Capacitor.getPlatform() === 'android') {
          return this.syncAndroid(contacts);
        }
        return of({ synced: contacts.length });
      }),
      tap(() => {
        localStorage.setItem(HASH_KEY, serverHash);
        console.log('[CallDirectory] Hash saved');
      })
    );
  }

  private syncIOS(contacts: CallDirectoryContact[]): Observable<any> {
    const payload = contacts.map(c => ({ phone: c.phone, label: c.label }));

    try {
      (window as any).webkit.messageHandlers.callDirectory.postMessage({
        action: 'sync',
        contacts: payload
      });
      console.log('[CallDirectory] Sent to native via messageHandlers');
    } catch (e) {
      console.error('[CallDirectory] messageHandlers error:', e);
      return of(undefined);
    }

    return this.syncResult$.pipe(
      take(1),
      timeout(10000),
      catchError(() => {
        console.warn('[CallDirectory] Native response timeout');
        return of({ synced: contacts.length, timeout: true });
      })
    );
  }

  private syncAndroid(contacts: CallDirectoryContact[]): Observable<any> {
    return new Observable(subscriber => {
      import('./call-directory.plugin').then(({ CallDirectory }) => {
        CallDirectory.syncContacts({ contacts }).then(
          (result: any) => {
            // Після першої синхронізації запитуємо роль CallScreening
            this.requestCallScreeningRole();
            subscriber.next(result);
            subscriber.complete();
          },
          (err: any) => { subscriber.error(err); }
        );
      }).catch(() => {
        subscriber.next(undefined);
        subscriber.complete();
      });
    });
  }

  private saveCount(count: number): void {
    localStorage.setItem(COUNT_KEY, String(count));
  }

  private restoreCount(): number | null {
    const raw = localStorage.getItem(COUNT_KEY);
    return raw ? Number(raw) : null;
  }

  private async requestCallScreeningRole() {
    try {
      const { CallDirectory } = await import('./call-directory.plugin');
      const { enabled } = await CallDirectory.isEnabled();
      if (!enabled) {
        console.log('[CallDirectory] Requesting CallScreening role...');
        const { granted } = await CallDirectory.requestRole();
        console.log('[CallDirectory] CallScreening role granted:', granted);
      }
    } catch (e) {
      console.warn('[CallDirectory] requestRole error:', e);
    }
  }

  async requestAllPermissions(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') return;
    try {
      const { CallDirectory } = await import('./call-directory.plugin');

      // 1. READ_PHONE_STATE + POST_NOTIFICATIONS через Capacitor
      await CallDirectory.requestPermissions();

      // 2. SYSTEM_ALERT_WINDOW — відкриває сторінку налаштувань
      const { granted } = await CallDirectory.checkOverlayPermission();
      if (!granted) {
        await CallDirectory.requestOverlayPermission();
      }
    } catch (e) {
      console.warn('[CallDirectory] permissions error:', e);
    }
  }

  async requestOverlayPermission(): Promise<boolean> {
    try {
      const { CallDirectory } = await import('./call-directory.plugin');
      const { granted } = await CallDirectory.checkOverlayPermission();
      if (granted) return true;
      const result = await CallDirectory.requestOverlayPermission();
      return result.granted;
    } catch (e) {
      console.warn('[CallDirectory] overlay permission error:', e);
      return false;
    }
  }

}
